import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FormStatus,
  SimpleEvent,
  ValidationContext,
} from '~/components/hoc/withValidation';
import InputField from '~/components/shared/Input/InputField';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store/types/sharedTypes';
import ValidationErrorMessage from '~/components/shared/ValidationErrorMessage/ValidationErrorMessage';
import { pushToGtmOnboardingAction } from '~/store/actions/gtmActions';
import { onChangeInputEvent } from '~/types/types';
import { LoanPurposes } from '~/enums';
import { updateForm } from '~/store/slices/onboardingApplication.slice';
import { Radio, Stack } from '@qred/components-library';
import { useLoanPurposeTranslationMapper } from '~/hooks/useLoanPurposeTranslationMapper';

const LoanPurpose = () => {
  const t = useTranslate();
  const dispatch = useDispatch();
  const loanPurposeTranslationMapper = useLoanPurposeTranslationMapper();

  const validationContext = useContext(ValidationContext);

  const { validationErrors } = validationContext;

  const otherLoanPurposeInput = useRef<HTMLInputElement>(null);

  const { form } = useSelector(
    (state: RootState) => state.onboardingApplication
  );

  const otherPurposeReasonTextMaxCharLimit = 190;

  useEffect(() => {
    const fakeEvent: SimpleEvent = {
      target: {
        name: 'LoanPurpose',
        value: form.purposeOfLoan || [],
        dataset: { validationType: 'LoanPurpose' },
      },
    };
    validationContext.onChangeHOC(fakeEvent);

    return () => {
      validationContext.removePropertyFromValidationErrors('LoanPurpose');
    };
  }, [form.purposeOfLoan]);

  useEffect(() => {
    if (otherLoanPurposeInput?.current) {
      otherLoanPurposeInput.current.scrollIntoView();
      otherLoanPurposeInput.current.focus();
    }
  }, [otherLoanPurposeInput?.current]);

  const options = [
    {
      label: loanPurposeTranslationMapper[LoanPurposes.Equipment],
      value: LoanPurposes.Equipment,
    },
    {
      label: loanPurposeTranslationMapper[LoanPurposes.RepayOtherLoan],
      value: LoanPurposes.RepayOtherLoan,
    },
    {
      label: loanPurposeTranslationMapper[LoanPurposes.Hiring],
      value: LoanPurposes.Hiring,
    },
    {
      label: loanPurposeTranslationMapper[LoanPurposes.Marketing],
      value: LoanPurposes.Marketing,
    },
    {
      label: loanPurposeTranslationMapper[LoanPurposes.Inventory],
      value: LoanPurposes.Inventory,
    },
    {
      label: loanPurposeTranslationMapper[LoanPurposes.Expansion],
      value: LoanPurposes.Expansion,
    },
    {
      label: loanPurposeTranslationMapper[LoanPurposes.Working],
      value: LoanPurposes.Working,
    },
    {
      label: loanPurposeTranslationMapper[LoanPurposes.Other],
      value: LoanPurposes.Other,
    },
  ];

  const validationErrorShouldBeShown =
    validationContext.formStatus === FormStatus.SUBMITTED &&
    validationErrors.LoanPurpose;

  const onChangePurposeHandler = (value: string) => {
    dispatch(
      updateForm({
        purposeOfLoan: value,
      })
    );

    if (value !== LoanPurposes.Other) {
      dispatch(
        updateForm({
          reason: '',
        })
      );
    }
    dispatch(
      pushToGtmOnboardingAction({
        stepActionNumber: 0.1,
        actionName: 'loan_purpose_change',
      })
    );
  };

  const onChangeOtherPurposeHandler = (event: onChangeInputEvent) => {
    dispatch(updateForm({ reason: event.target.value }));
  };

  const onBlurOtherPurposeHandler = () => {
    dispatch(
      pushToGtmOnboardingAction({
        actionName: 'loan_purpose_other_change',
        stepActionNumber: 0.2,
      })
    );
  };

  return (
    <Stack spacing={'md'}>
      <Radio.Group
        name="purposes of loan"
        onChange={onChangePurposeHandler}
        value={form.purposeOfLoan}
        direction="column"
      >
        <Stack spacing={'lg'}>
          {options.map((option) => (
            <Radio
              key={option.value}
              value={option.value}
              label={option.label}
              dataCy="onboarding_loan_purpose"
            />
          ))}
        </Stack>
      </Radio.Group>

      {validationErrorShouldBeShown && (
        <ValidationErrorMessage>
          {t('ValidationErrors.LoanPurpose')}
        </ValidationErrorMessage>
      )}
      {form.purposeOfLoan === LoanPurposes.Other && (
        <InputField
          name="SpecifyLoanPurpose"
          dataCy="other_loan_purpose"
          onChange={onChangeOtherPurposeHandler}
          onBlur={onBlurOtherPurposeHandler}
          validationType="LoanPurpose"
          placeholder={t('Onboarding.LoanPurposeInputPlaceholder') as string}
          maxLength={otherPurposeReasonTextMaxCharLimit}
          value={form.reason || ''}
          ref={otherLoanPurposeInput}
          characterCounter
        />
      )}
    </Stack>
  );
};

export default LoanPurpose;
